// src/components/NavSearch/NavSearch.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./NavSearch.css";
import MukemelLogo from "./../../assets/headernewlogo2.png";
import LatesProduct from "../LatestProduct/LatesProduct";
import { useTranslation } from "react-i18next";
import categoryData from "../../api/categories";
import { formatPage } from "../../utils/formatPage";
import { useCurrency } from "./../../components/PriceFormat/PriceFormat";
import {
  pickDisplayPrice,
  formatMoney as fmtMoney,
} from "../../utils/pricePicker";
import { loadProductsByLang } from "../../utils/productLoader";

const normalizeCurr = (c) => {
  const up = String(c || "TRY").toUpperCase();
  return up === "TL" ? "TRY" : up;
};

function isPerfumeProduct(p) {
  const norm = (x) =>
    String(x || "")
      .normalize("NFKD")
      .toLowerCase()
      .trim();
  const c = p?.category;
  if (Array.isArray(c)) {
    return c.some((cat) => {
      const n = norm(cat);
      return n === "perfume" || n === "parfum" || n === "parfüm";
    });
  }
  if (typeof c === "string") {
    const n = norm(c);
    return (
      n.includes("perfume") || n.includes("parfum") || n.includes("parfüm")
    );
  }
  return false;
}

// Parfüm için manual_prices öncelikli; değilse pickDisplayPrice
const pickPerfAwarePrice = (product, currency) => {
  const curr = normalizeCurr(currency);
  if (isPerfumeProduct(product)) {
    const mp = product?.manual_prices || product?.manualPrices;
    const v = mp?.[curr];
    if (v != null && !Number.isNaN(Number(v))) {
      return { amount: Number(v), curr };
    }
  }
  return pickDisplayPrice(product, curr);
};

function formatMoney(amount, curr) {
  const up = String(curr).toUpperCase();
  const isTRY = up === "TRY" || up === "TL";
  const opts = isTRY
    ? amount < 1000
      ? { min: 1, max: 1 }
      : { min: 0, max: 0 }
    : { min: 0, max: 0 };
  return fmtMoney(amount, curr, opts);
}
/* ---------------------------------------------- */

const NavSearch = () => {
  const { t, i18n } = useTranslation();
  const { currency } = useCurrency();

  const [searchText, setSearchText] = useState("");
  const [index, setIndex] = useState(0);
  const [products, setProducts] = useState([]);

  // 🔹 Fiyat indeksi: id -> { dp, mp }
  const [priceIndex, setPriceIndex] = useState({});
  const [pricesReady, setPricesReady] = useState(false);

  // 🔹 Localized isimler için indeks (TR/AR)
  const [localizedMap, setLocalizedMap] = useState(new Map());

  // Modal ilk kullanıma geldiğinde tüm ürünleri çek ve fiyat indeksini oluştur
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/products`
        );
        if (dead) return;
        const map = {};
        (Array.isArray(data) ? data : []).forEach((p) => {
          if (!p) return;
          map[p.id] = {
            dp: p.displayPrices || p.displayprices || null,
            mp: p.manual_prices || p.manualPrices || null,
          };
        });
        setPriceIndex(map);
      } catch (e) {
        // sessiz geç; indeks boş kalırsa yine de çalışır, sadece "-" görürüz
      } finally {
        if (!dead) setPricesReady(true);
      }
    })();
    return () => {
      dead = true;
    };
  }, []);

  // 🔹 Dil değiştiğinde localized ürün isimlerini yükle
  useEffect(() => {
    let dead = false;
    const lang = (
      String(i18n?.language || "en").split("-")[0] || "en"
    ).toLowerCase();

    if (lang === "en") {
      setLocalizedMap(new Map());
      return;
    }

    (async () => {
      try {
        const localized = await loadProductsByLang(lang);
        if (dead) return;
        const map = new Map(
          (Array.isArray(localized) ? localized : []).map((p) => [
            p.stock_code || String(p.id),
            p,
          ])
        );
        setLocalizedMap(map);
      } catch {
        if (!dead) setLocalizedMap(new Map());
      }
    })();

    return () => {
      dead = true;
    };
  }, [i18n.language]);

  const placeholderTexts = [
    t("Perfume"),
    t("Prestige"),
    t("Space Stylers"),
    t("Monuments"),
    t("Essence Of Form"),
    t("Art In Form"),
    t("Flame Holders"),
    t("Wall Masterpieces"),
    t("Read Display"),
    t("Light Style"),
    t("A Token Of Love"),
    t("Warm Sips"),
    t("Garden Charm"),
    t("Desk Elegance"),
  ];

  useEffect(() => {
    const iv = setInterval(
      () => setIndex((p) => (p + 1) % placeholderTexts.length),
      3000
    );
    return () => clearInterval(iv);
  }, [placeholderTexts.length]);

  // Arama
  const handleSearch = useCallback(async () => {
    const q = searchText.trim();
    if (!q) return;
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/products-filter`,
        { search: q }
      );
      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("search error:", error);
      setProducts([]);
    }
  }, [searchText]);

  useEffect(() => {
    const tmr = setTimeout(() => {
      if (searchText.trim()) handleSearch();
    }, 150);
    return () => clearTimeout(tmr);
  }, [searchText, handleSearch]);

  // Input temizlenince sonuçları da temizle → sadece Latest kalsın
  useEffect(() => {
    if (searchText.trim() === "") setProducts([]);
  }, [searchText]);

  const getProductImage = (product) => {
    return (
      product?.default_images?.[0] + "?v=123456" ||
      product?.color_images?.[0]?.images?.[0] + "?v=123456" ||
      "https://apimukemel.com/api/assets/BTC0001-1.webp"
    );
  };

  // 🔹 Ürünün localized adını al
  const getProductName = (product) => {
    const key = product?.stock_code || String(product?.id);
    const localized = localizedMap.get(key);
    return localized?.name || product?.name || "";
  };

  const renderPrice = (product) => {
    // İndeksten fiyat bilgilerini al
    const idx = priceIndex?.[product?.id];
    const dpFromIdx = idx?.dp || null;
    const mpFromIdx = idx?.mp || null;

    // Ürünün kendi fiyat bilgileri + indeksten yedekler
    const productWithPrices = {
      ...product,
      displayPrices:
        product?.displayPrices || product?.displayprices || dpFromIdx,
      manual_prices:
        product?.manual_prices || product?.manualPrices || mpFromIdx,
    };

    const picked = pickPerfAwarePrice(productWithPrices, currency);
    if (!picked) return "-";
    return formatMoney(picked.amount, picked.curr);
  };

  return (
    <>
      <div
        className="modal fade"
        id="searchModal"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-fullscreen">
          <div className="modal-content">
            <div className="modal-header d-flex justify-content-center align-items-center border-0">
              <h5 className="modal-title mt-3" id="exampleModalLabel">
                <a href="/">
                  <img src={MukemelLogo} alt="Mukemel" width={100} />
                </a>
              </h5>
              <button
                type="button"
                className="btn-close position-absolute end-0 me-3"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>

            <div className="modal-body pe-0 px-0">
              <div className="container">
                <div className="row justify-content-center">
                  <div className="col-12 col-md-6 col-sm-8 col-lg-8">
                    <form
                      className="d-flex mb-3 position-relative"
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSearch();
                      }}
                    >
                      <input
                        className="form-control me-2 rounded-2 p-3 pe-5 animated-placeholder"
                        type="search"
                        placeholder={`${t("Search for")} ${
                          placeholderTexts[index]
                        }`}
                        aria-label="Search"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                      {searchText && (
                        <span
                          className="position-absolute end-0 top-50 translate-middle-y me-4 text-secondary fs-11"
                          style={{ cursor: "pointer" }}
                          onClick={() => setSearchText("")}
                        >
                          {t("Clear")}
                        </span>
                      )}
                    </form>

                    <div className="text-center mb-4">
                      <span className="fs-10 text-dark text-uppercase mx-3">
                        {t("Trending Searches")}
                      </span>
                      {categoryData.category.slice(0, 4).map((cat) => (
                        <a
                          key={cat.id}
                          href={`/category/${formatPage(cat.categoryname)}`}
                          className="text-dark text-decoration-none fw-14 mx-2"
                        >
                          {t(cat.translationKey)}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Arama kutusu DOLUYSA sonuçlar */}
              {searchText.trim() !== "" && (
                <div className="container-fluid">
                  <div className="row g-0">
                    {products.length > 0 ? (
                      products.map((product) => {
                        const inStock = String(product?.availability || "")
                          .toLowerCase()
                          .includes("in stock");
                        return (
                          <div
                            key={product.id}
                            className="col-lg-3 col-md-6 col-sm-6 col-12 mb-4 pe-0 px-0 mt-4"
                          >
                            <a
                              href={`/product/${product.id}`}
                              className="text-decoration-none"
                            >
                              <div className="cardbg">
                                <img
                                  src={getProductImage(product)}
                                  alt={product.name}
                                  className="d-block w-100"
                                />
                              </div>
                              <div className="px-4 bg-light pt-2 pb-2">
                                <h5 className="mb-0 text-dark">
                                  {getProductName(product)}
                                </h5>
                                {inStock ? (
                                  <h6 className="text-muted mt-1">
                                    {renderPrice(product)}
                                  </h6>
                                ) : (
                                  <h6 className="fw-bold mt-1 text-danger">
                                    {t("Out Of Stock")}
                                  </h6>
                                )}
                              </div>
                            </a>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-5 text-muted w-100">
                        {pricesReady ? t("ProductNotFound") : t("Loading...")}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Arama kutusu BOŞSA sadece Latest Added Products */}
              {searchText.trim() === "" && (
                <div className="container-fluid mt-5 pe-0 px-0">
                  <h6 className="fw-normal mx-5">
                    {t("Latest Added Products")}
                  </h6>
                  <div className="row pe-0 px-0 g-0">
                    <LatesProduct />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NavSearch;
